import { Injectable, signal, WritableSignal } from '@angular/core';
import { LocalStorageKey } from '@constants/local-storage.constants';

export type AppTheme = 'light' | 'dark';
export type SelectableTheme = 'system' | AppTheme;

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    public theme: WritableSignal<AppTheme | null> = signal(null);
    public selectedTheme: WritableSignal<SelectableTheme | null> = signal(null);

    public initTheme(): void {
        const theme = localStorage.getItem(LocalStorageKey.theme);
        if (theme) {
            this.changeTheme(theme as SelectableTheme);
        } else {
            this.changeTheme('dark', false);
        }

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', ({ matches }) => {
            if (this.selectedTheme() === 'system') {
                this.applyTheme(matches ? 'dark' : 'light');
            }
        });
    }

    public changeTheme(theme: SelectableTheme, saveTheme = true): void {
        this.selectedTheme.set(theme);

        if (theme === 'light' || theme === 'dark') {
            this.applyTheme(theme);
        } else if (theme === 'system') {
            this.applySystemPreference();
        }

        if (saveTheme) {
            this.saveSelectedThemeToStorage();
        }
    }

    public toggleTheme(): void {
        this.changeTheme(this.theme() === 'light' ? 'dark' : 'light', true);
    }

    private saveSelectedThemeToStorage(): void {
        localStorage.setItem(LocalStorageKey.theme, this.selectedTheme() as SelectableTheme);
    }

    private applyTheme(theme: AppTheme) {
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        this.theme.set(theme);
    }

    private applySystemPreference() {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.applyTheme(prefersDark ? 'dark' : 'light');
    }
}
